import { forwardRef, type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react'
import { Loader2 } from 'lucide-react'
import { cx } from './cx.ts'

export type ButtonKind = 'primary' | 'forest' | 'ghost' | 'danger' | 'ink' | 'subtle' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

const kinds: Record<ButtonKind, string> = {
  primary: 'bg-gold-bright text-ink hover:brightness-95 disabled:opacity-60',
  forest: 'bg-forest text-white hover:bg-forest-deep disabled:opacity-60',
  ghost: 'bg-transparent text-forest border-[1.5px] border-forest hover:bg-forest/5 disabled:opacity-50',
  danger: 'bg-live text-white hover:brightness-95 disabled:opacity-60',
  ink: 'bg-ink text-cream hover:bg-navsurf disabled:opacity-60',
  subtle: 'bg-mist text-text hover:bg-[#e8e5dd] disabled:opacity-60',
  link: 'bg-transparent text-forest underline-offset-2 hover:underline px-0! min-h-0!',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'min-h-11 lg:min-h-9 px-3.5 text-[13px] gap-1.5',
  md: 'min-h-11 px-5 text-[14.5px] gap-2',
  lg: 'min-h-12 px-6 text-[15px] gap-2',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: ButtonKind
  size?: ButtonSize
  icon?: ReactNode
  iconRight?: ReactNode
  loading?: boolean
  full?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { kind = 'forest', size = 'md', icon, iconRight, loading, full, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center rounded-full font-bold whitespace-nowrap transition select-none',
        kinds[kind],
        sizes[size],
        full && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
      {iconRight}
    </button>
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  children: ReactNode
  tone?: 'default' | 'light'
  ref?: Ref<HTMLButtonElement>
}

/** Square 44px button with an accessible label. */
export function IconButton({ label, children, className, tone = 'default', type = 'button', ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-full transition disabled:opacity-40',
        tone === 'light' ? 'text-cream hover:bg-white/10' : 'text-muted hover:bg-mist hover:text-text',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
